import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import { useOrgId } from '@/common/hooks/useOrgId';
import { fetchIncrementalBatch } from '@/common/queries/fetchIncrementalBatch';
import { ObjectiveHealthApi } from '@/features/objectives/api/objectiveHealth';
import { objectiveHealthQueryKeys } from '@/features/objectives/queryKeys/objectiveHealth';

const OBJECTIVE_HEALTH_BATCH_SIZE = 200;

export const invalidateObjectiveHealth = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: objectiveHealthQueryKeys.objectives.all() });

export const useObjectiveHealthByIdsQuery = (
  objectiveIds: number[],
  options?: Omit<UseQueryOptions<Record<number, ObjectiveHealth>, Error>, 'queryFn' | 'queryKey'>,
) => {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: objectiveHealthQueryKeys.objectives.byIds(orgId, objectiveIds),
    queryFn: () =>
      fetchIncrementalBatch<ObjectiveHealth>(queryClient, objectiveIds, {
        queryKeyFn: (id) => objectiveHealthQueryKeys.objectives.byId(orgId, id),
        apiFn: async (ids) => {
          const response = await ObjectiveHealthApi.getByIds(ids);
          return Object.fromEntries(response.data.map((h) => [h.objectiveId, h]));
        },
        batchSize: OBJECTIVE_HEALTH_BATCH_SIZE,
        staleTime: options?.staleTime as number | undefined,
      }),
    enabled: objectiveIds.length > 0,
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

export const useObjectiveHealthByIdQuery = (
  objectiveId: number,
  { loopId, enabled = true }: { loopId?: number; enabled?: boolean } = {},
) => {
  const orgId = useOrgId();

  return useQuery({
    queryKey: objectiveHealthQueryKeys.objectives.byId(orgId, objectiveId, loopId),
    queryFn: () => ObjectiveHealthApi.getById(objectiveId, { loopId }),
    enabled,
  });
};
